/**
 * WorkOrderNotesHistory Component
 *
 * Shows booking notes (with star/pin) and work order status change history.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CancelIcon from '@mui/icons-material/Cancel';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShieldIcon from '@mui/icons-material/Shield';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { format } from 'date-fns';
import type { NoteCategory, StatusHistoryEntry, WorkOrderNote } from '@/types';
import { useWorkOrderFlow } from '@/hooks/useWorkOrderFlow';
import { neutralGrey } from '@/styles/theme';
import { formatHistoryReason } from './formatHistoryReason';
import { dateFnsLocale } from '@/utils/locale';

const CATEGORY_LABEL_KEY: Record<NoteCategory, string> = {
  customer: 'notesHistory.customerTab',
  internal: 'notesHistory.internalTab',
  manager: 'notesHistory.managerTab',
};

function byCreatedAtDesc(a: WorkOrderNote, b: WorkOrderNote): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function isNotePinned(note: WorkOrderNote): boolean {
  return note.isPinned === true;
}

function groupNotesByPin(notes: WorkOrderNote[]): {
  pinned: WorkOrderNote[];
  unpinned: WorkOrderNote[];
} {
  const pinned = notes.filter(isNotePinned).sort(byCreatedAtDesc);
  const unpinned = notes.filter((note) => !isNotePinned(note)).sort(byCreatedAtDesc);
  return { pinned, unpinned };
}

export interface WorkOrderNotesListProps {
  notes: WorkOrderNote[];
  onTogglePin?: (noteId: string, pinned: boolean) => void;
}

export function WorkOrderNotesList({ notes, onTogglePin }: WorkOrderNotesListProps) {
  const { t } = useTranslation('workorders');
  const dateLocale = dateFnsLocale();
  const { pinned, unpinned } = useMemo(() => groupNotesByPin(notes), [notes]);
  const pinnedCount = pinned.length;

  const renderNote = (note: WorkOrderNote) => {
    const starred = isNotePinned(note);
    const starLabel = starred ? t('notesHistory.unpinNote') : t('notesHistory.pinNote');
    return (
      <ListItem
        key={note.id}
        data-testid={`note-${note.id}`}
        data-category={note.category}
        data-pinned={starred ? 'true' : 'false'}
        secondaryAction={
          <Tooltip title={starLabel}>
            <span>
              <IconButton
                edge="end"
                size="small"
                onClick={() => {
                  onTogglePin?.(note.id, !starred);
                }}
                disabled={onTogglePin === undefined}
                aria-label={starLabel}
                aria-pressed={starred}
              >
                {starred ? (
                  <StarIcon fontSize="small" color="warning" />
                ) : (
                  <StarBorderIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        }
        sx={{
          alignItems: 'flex-start',
          px: 1,
          py: 1,
          mb: 0.5,
          borderRadius: 1,
          borderLeft: starred ? '3px solid' : '3px solid transparent',
          borderColor: starred ? 'warning.main' : 'transparent',
          bgcolor: starred ? 'action.selected' : undefined,
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', pr: 4 }}>
              {starred && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                  }}
                >
                  {t('notesHistory.pinned')}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                {t(CATEGORY_LABEL_KEY[note.category])}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                {note.content}
              </Typography>
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {note.authorName ?? t('statusHistory.system')} •{' '}
              {format(new Date(note.createdAt), 'd.M.yyyy HH:mm', { locale: dateLocale })}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  if (notes.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        {t('notesHistory.noNotes')}
      </Typography>
    );
  }

  return (
    <Box>
      {pinnedCount > 0 && (
        <Typography
          variant="body2"
          color="warning.main"
          role="status"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, fontWeight: 600 }}
          data-testid="pinned-notes-summary"
        >
          <StarIcon fontSize="small" aria-hidden />
          {t('notesHistory.pinnedCount', { count: pinnedCount })}
        </Typography>
      )}
      <List disablePadding>
        {pinned.length > 0 && (
          <ListSubheader disableSticky sx={{ px: 1, lineHeight: 2 }}>
            {t('notesHistory.pinnedGroup')}
          </ListSubheader>
        )}
        {pinned.map(renderNote)}
        {unpinned.length > 0 && pinned.length > 0 && (
          <ListSubheader disableSticky sx={{ px: 1, lineHeight: 2 }}>
            {t('notesHistory.otherNotes')}
          </ListSubheader>
        )}
        {unpinned.map(renderNote)}
      </List>
    </Box>
  );
}

interface WorkOrderNotesHistoryProps {
  history: StatusHistoryEntry[];
  cardVariant?: 'elevation' | 'outlined';
  notes?: WorkOrderNote[];
  onTogglePin?: (noteId: string, pinned: boolean) => void;
}

// Map newStatus to icon (includes legacy values for old history entries)
const statusIconMap: Record<string, React.ReactNode> = {
  quote: <RequestQuoteIcon fontSize="small" />,
  pending: <AssignmentIcon fontSize="small" />,
  confirmed: <ConfirmationNumberIcon fontSize="small" />,
  vehicle_arrived: <DirectionsCarIcon fontSize="small" />,
  in_progress: <PlayArrowIcon fontSize="small" />,
  paused: <PauseIcon fontSize="small" />,
  waiting_parts: <LocalShippingIcon fontSize="small" />,
  parts_received: <Inventory2Icon fontSize="small" />,
  work_completed: <CheckCircleIcon fontSize="small" />,
  ready_to_invoice: <ReceiptLongIcon fontSize="small" />,
  invoiced: <ReceiptLongIcon fontSize="small" />,
  completed: <CheckCircleIcon fontSize="small" />,
  cancelled: <CancelIcon fontSize="small" />,
  quote_rejected: <ThumbDownIcon fontSize="small" />,
  // Legacy (old history entries may reference these)
  warranty_claim: <ShieldIcon fontSize="small" />,
  disputed: <ReportProblemIcon fontSize="small" />,
  invoicing_hold: <HourglassBottomIcon fontSize="small" />,
};

function getStatusIcon(status: string): React.ReactNode {
  return statusIconMap[status] ?? <HistoryIcon fontSize="small" />;
}

export function WorkOrderNotesHistory({
  history,
  cardVariant = 'elevation',
  notes,
  onTogglePin,
}: WorkOrderNotesHistoryProps) {
  const { t } = useTranslation('workorders');
  const { statusColors } = useWorkOrderFlow();

  const sortedHistory = useMemo(() => {
    return [...history].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [history]);

  return (
    <Card variant={cardVariant}>
      <CardContent>
        {notes !== undefined && (
          <Box sx={{ mb: sortedHistory.length > 0 ? 2 : 0 }}>
            <Typography variant="h6" gutterBottom>
              {t('notesHistory.notesTitle')}
            </Typography>
            <WorkOrderNotesList
              notes={notes}
              {...(onTogglePin !== undefined ? { onTogglePin } : {})}
            />
          </Box>
        )}
        <Typography variant="h6" gutterBottom>
          {t('history')}
        </Typography>

        {sortedHistory.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('notesHistory.noHistory')}</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            <List disablePadding>
              {sortedHistory.map((entry, index) => {
                const fieldReason = formatHistoryReason(entry.reasonForChange, t);
                const isFieldChange = entry.oldStatus === entry.newStatus && fieldReason != null;
                const color = statusColors[entry.newStatus] ?? neutralGrey;
                const isFirst = index === 0;
                const isLast = index === sortedHistory.length - 1;
                return (
                  <ListItem
                    key={entry.id}
                    sx={{
                      px: 0,
                      py: 1.5,
                      alignItems: 'flex-start',
                      position: 'relative',
                    }}
                  >
                    {/* Incoming connector from previous entry */}
                    {!isFirst && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 17,
                          top: 0,
                          height: 12,
                          width: 2,
                          bgcolor: 'divider',
                        }}
                      />
                    )}
                    {/* Outgoing connector to next entry */}
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 17,
                          top: 48,
                          bottom: 0,
                          width: 2,
                          bgcolor: 'divider',
                        }}
                      />
                    )}
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: alpha(color, 0.12),
                          color,
                        }}
                      >
                        {getStatusIcon(entry.newStatus)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2">
                            {isFieldChange
                              ? fieldReason
                              : t(`status.${entry.newStatus}`, entry.newStatus)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {entry.changedByName ?? t('statusHistory.system')} •{' '}
                            {format(new Date(entry.createdAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
